import { useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Paper,
  RangeSlider,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { Plus } from 'react-feather';
import { groupsRange, rangeLabel } from '../../model/derive';
import {
  ruleConflict,
  ruleDescription,
  ruleIssue,
  ruleTitle,
} from '../../model/goals';
import { hasCoachData } from '../../model/defaults';
import { SERVER_SUPPORTS } from '../../model/serverSupport';
import { columnOf } from '../../model/review';
import type { MatchingConfig } from '../../model/types';
import { useSetup } from '../../SetupContext';
import { AddRequirementModal } from './AddRequirementModal';

const SIZE_MIN = 3;
const SIZE_MAX = 12;

const ENFORCE_ROWS: {
  key: keyof MatchingConfig['enforce'];
  title: string;
  description: string;
}[] = [
  {
    key: 'doNotMatch',
    title: 'Enforce do-not-matches',
    description:
      "People on a do-not-match list are never in the same group. Turn off only if matching can't succeed with them.",
  },
  {
    key: 'coachCapacity',
    title: 'Enforce coach capacity',
    description: 'Never make more groups than your coaches can cover.',
  },
  {
    key: 'availability',
    title: 'Enforce participant availability',
    description:
      'People are only grouped when they share a time that works for them.',
  },
];

function RequirementRow({
  title,
  description,
  enabled,
  note,
  warning,
  onToggle,
  onDelete,
}: {
  title: string;
  description: string;
  enabled: boolean;
  /** Why the switch can't be changed, shown under the description. */
  note?: string;
  warning?: string;
  onToggle?: () => void;
  onDelete?: () => void;
}) {
  return (
    <Paper withBorder radius="md" px={20} py={16}>
      <Group wrap="nowrap" gap="md">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600}>{title}</Text>
          <Text size="sm" c="dimmed" mt={2}>
            {description}
          </Text>
          {note && (
            <Text size="sm" c="warningAmber.8" fw={500} mt={4}>
              {note}
            </Text>
          )}
          {warning && (
            <Alert color="dangerRed" variant="light" mt="xs" p="xs">
              <Text size="sm" fw={600} c="dangerRed.9">
                {warning}
              </Text>
            </Alert>
          )}
        </Box>
        {onDelete && (
          <Button variant="default" size="sm" onClick={onDelete}>
            Delete requirement
          </Button>
        )}
        <Switch
          size="md"
          checked={enabled}
          onChange={onToggle}
          disabled={!onToggle}
          aria-label={title}
        />
      </Group>
    </Paper>
  );
}

export function GroupRequirementsCard() {
  const { session, config, update, values, readOnly } = useSetup();
  const [adding, setAdding] = useState(false);
  const n = session.participants.length;
  const groups = groupsRange(n, config.groupSize);

  return (
    <Card withBorder p={0} radius="lg">
      <Box px={28} pt={24} pb={18}>
        <Title order={2} c="medleyGreen.8">
          Group requirements
        </Title>
        <Text c="dimmed" mt={6}>
          Group requirements are things we hold as required for every group, so
          you will never see a matching that breaks one. If a requirement can't
          be met, matching stops. Because of this, use requirements sparingly.
        </Text>
      </Box>
      <Divider />
      <Stack gap="sm" px={28} py={24}>
        <Paper withBorder radius="md" px={20} py={18}>
          <Group justify="space-between" align="flex-start">
            <div>
              <Text fw={600}>Group size</Text>
              <Text size="sm" c="dimmed" mt={2}>
                Always enforced. Every group has between these two sizes.
              </Text>
            </div>
            <Badge color="gray" variant="light">
              Enforced
            </Badge>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={28} mt="md">
            <div>
              <Group justify="space-between" mb="xs">
                <Text fw={500} size="sm" id="group-size-label">
                  Smallest and largest group
                </Text>
                <Text size="sm">
                  {rangeLabel(config.groupSize.min, config.groupSize.max)}{' '}
                  people
                </Text>
              </Group>
              <RangeSlider
                aria-labelledby="group-size-label"
                min={SIZE_MIN}
                max={SIZE_MAX}
                step={1}
                minRange={0}
                value={[config.groupSize.min, config.groupSize.max]}
                onChange={([min, max]) =>
                  update({ type: 'setGroupSize', min, max })
                }
                disabled={readOnly}
                marks={[
                  { value: SIZE_MIN, label: String(SIZE_MIN) },
                  { value: SIZE_MAX, label: String(SIZE_MAX) },
                ]}
                mb="lg"
              />
            </div>
            <Paper bg="gray.0" withBorder p="sm" radius="md">
              <Text size="sm">
                Every group has{' '}
                {rangeLabel(config.groupSize.min, config.groupSize.max)} people.
              </Text>
              <Text size="sm" c="dimmed">
                That gives {groups.label} groups for {n} participants.
              </Text>
            </Paper>
          </SimpleGrid>
        </Paper>

        {ENFORCE_ROWS.filter(
          // Hidden until matching-server reads enforce.availability.
          r => r.key !== 'availability' || SERVER_SUPPORTS.availabilitySwitch
        ).map(r => {
          // Without coach data, coach capacity can't be enforced: the switch
          // stays off and can't be turned on.
          const noCoachData =
            r.key === 'coachCapacity' && !hasCoachData(session);
          return (
            <RequirementRow
              key={r.key}
              title={r.title}
              description={r.description}
              enabled={noCoachData ? false : config.enforce[r.key]}
              note={
                noCoachData
                  ? "This matching session has no coach data, so coach capacity can't be enforced."
                  : undefined
              }
              onToggle={
                readOnly || noCoachData
                  ? undefined
                  : () =>
                      update({
                        type: 'setEnforce',
                        key: r.key,
                        value: !config.enforce[r.key],
                      })
              }
            />
          );
        })}

        {config.columnRules.map((rule, index) => {
          const column = columnOf(session, rule.column);
          const vals = values[rule.column] ?? [];
          const warning =
            ruleConflict(rule, config, column, vals) ||
            ruleIssue(rule, config, column, vals, n);
          return (
            <RequirementRow
              key={rule.column}
              title={ruleTitle(rule, column)}
              description={ruleDescription(rule, column)}
              enabled={rule.enabled}
              warning={warning || undefined}
              onToggle={
                readOnly
                  ? undefined
                  : () => update({ type: 'toggleColumnRule', index })
              }
              onDelete={
                readOnly || rule.builtin
                  ? undefined
                  : () => update({ type: 'removeColumnRule', index })
              }
            />
          );
        })}

        {!readOnly && (
          <Button
            variant="outline"
            leftSection={<Plus size={16} />}
            style={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}
            onClick={() => setAdding(true)}
          >
            Add a group requirement
          </Button>
        )}
      </Stack>
      {adding && <AddRequirementModal onClose={() => setAdding(false)} />}
    </Card>
  );
}
